
import { ValidationResult } from './types';
import { Category } from '@/types/assessment';

/**
 * Check if a question has been fully answered
 */
export function isQuestionFullyAnswered(response: any): boolean {
  if (!response) return false;
  
  // For simple responses
  if (response.current !== undefined && response.target !== undefined) {
    return true;
  }
  
  // For older response format that might just have 'value'
  if (response.value !== undefined) {
    return true;
  }
  
  return false;
}

/**
 * Check if the user can move forward from the current question
 */
export function canMoveForwardFromQuestion(currentQuestion: any, responses: Record<string, any>): boolean {
  // Allow moving forward even if the question isn't answered
  // This enables users to navigate through all questions and come back to unanswered ones later
  return true;
}

/**
 * Validate that all required questions have been answered
 */
export function validateAllRequiredQuestionsAnswered(
  responses: Record<string, any>,
  categories: Category[]
): ValidationResult {
  const unansweredQuestions: string[] = [];
  
  categories.forEach(category => {
    category.questions.forEach(question => {
      // Only check required questions
      if (question.required && !isQuestionFullyAnswered(responses[question.id])) {
        unansweredQuestions.push(question.id);
      }
    });
  });
  
  return {
    valid: unansweredQuestions.length === 0,
    unansweredQuestions
  };
}

/**
 * Calculate scores by category based on responses
 */
export function calculateScoresByCategory(
  responses: Record<string, any>,
  categories: Category[],
  assessmentType: string
): Record<string, {current: number, target: number}> {
  const scoresByCategory: Record<string, {current: number, target: number}> = {};
  
  categories.forEach(category => {
    let currentTotal = 0;
    let targetTotal = 0;
    let count = 0;
    
    // Calculate scores for each answered question in this category
    category.questions.forEach(question => {
      const response = responses[question.id];
      
      if (response) {
        // Handle different response formats for CURRENT value
        let currentVal = 0;
        if (typeof response.current === 'number') {
          currentVal = response.current;
        } else if (Array.isArray(response.current)) {
          if (response.current.length > 0) {
            // Map current answer IDs to their numeric values
            const currentValues = response.current.map((id: string) => {
              const answer = question.answers.find(a => a.id === id);
              return answer ? answer.value : 0;
            });
            currentVal = Math.max(...currentValues);
          } else {
            currentVal = 0;
          }
        } else if (response.value !== undefined) {
          currentVal = response.value;
        } else {
          currentVal = 0;
        }
              
        // Handle different response formats for TARGET value
        let targetVal = 0;
        if (typeof response.target === 'number') {
          targetVal = response.target;
        } else if (Array.isArray(response.target)) {
          if (response.target.length > 0) {
            // Map target answer IDs to their numeric values
            const targetValues = response.target.map((id: string) => {
              const answer = question.answers.find(a => a.id === id);
              return answer ? answer.value : 0;
            });
            targetVal = Math.max(...targetValues);
          } else {
            targetVal = assessmentType === 'maturity' ? 4 : 0; // Default target
          }
        } else {
          targetVal = assessmentType === 'maturity' ? 4 : 0; // Default target
        }
        
        currentTotal += currentVal;
        targetTotal += targetVal;
        count++;
      }
    });
    
    // Calculate average scores if there are answers
    if (count > 0) {
      const currentAvg = currentTotal / count;
      const targetAvg = targetTotal / count;
      
      scoresByCategory[category.id] = {
        current: currentAvg,
        target: targetAvg
      };
    } else {
      // Default values if no questions answered
      scoresByCategory[category.id] = {
        current: 0,
        target: assessmentType === 'maturity' ? 4 : 0
      };
    }
  });
  
  return scoresByCategory;
}
